// AdminDashboard.js
// Internal admin panel — only accessible to users with role: 'admin' in Firestore.
// Set role manually in Firebase Console; never through the app.
// Route: /admin (no ProtectedRoute wrapper — auth handled internally to support 'admin' role)

import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';

function AdminDashboard() {
  const navigate = useNavigate();

  // ===== AUTH / ROLE STATE =====
  // checking stays true until admin role is confirmed — nothing renders until then
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin]   = useState(false);

  // ===== DATA STATE =====
  const [expansionRequests, setExpansionRequests] = useState([]); // raw Firestore docs
  const [expandedCity, setExpandedCity]           = useState(null); // key of open city row
  const [metrics, setMetrics]                     = useState(null); // key metrics object
  const [recentSignups, setRecentSignups]         = useState([]);   // last 10 users

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  // ===== STEP 1 — ADMIN ROLE CHECK =====
  // Runs on mount. Redirects immediately if not logged in or not admin.
  // Never shows any content before this resolves.
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/');
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (!userSnap.exists() || userSnap.data().role !== 'admin') {
          navigate('/');
          return;
        }

        // Role confirmed — allow data fetch
        setIsAdmin(true);
        setChecking(false);
      } catch (err) {
        console.error('Admin role check error:', err);
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // ===== STEP 2 — FETCH ALL DATA =====
  // Only runs after isAdmin is confirmed. Fires all queries in parallel.
  useEffect(() => {
    if (!isAdmin) return;

    const fetchAll = async () => {
      try {
        setLoading(true);

        // All independent Firestore reads in a single Promise.all
        const [
          expansionSnap,
          customersSnap,
          providersSnap,
          allBookingsSnap,
          outOfAreaSnap,
          recentSnap,
        ] = await Promise.all([
          getDocs(collection(db, 'expansionRequests')),
          getDocs(query(collection(db, 'users'), where('role', '==', 'customer'))),
          getDocs(query(collection(db, 'users'), where('role', '==', 'provider'))),
          getDocs(collection(db, 'bookings')),
          getDocs(query(collection(db, 'users'), where('outOfArea', '==', true))),
          getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(10))),
        ]);

        // ---- Expansion requests — store raw docs for grouping ----
        setExpansionRequests(expansionSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // ---- Key metrics ----
        const allBookings  = allBookingsSnap.docs.map(d => d.data());
        const paidBookings = allBookings.filter(b => !!b.paidAt);
        const totalRevenue = paidBookings.reduce((sum, b) => sum + (b.price || 0), 0);

        setMetrics({
          customers:    customersSnap.size,
          providers:    providersSnap.size,
          bookings:     allBookingsSnap.size,
          paidBookings: paidBookings.length,
          revenue:      totalRevenue,   // stored in cents
          outOfArea:    outOfAreaSnap.size,
        });

        // ---- Recent signups ----
        setRecentSignups(recentSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      } catch (err) {
        console.error('Admin data fetch error:', err);
        setError('Failed to load data. Make sure Firestore rules allow admin reads on all collections.');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [isAdmin]);

  // ===== HELPERS =====

  // Group expansion requests by "city|state" key, sorted by count desc
  const groupedRequests = () => {
    const groups = {};

    expansionRequests.forEach(req => {
      const city  = (req.city  || '').toLowerCase().trim();
      const state = (req.state || '').toLowerCase().trim();
      const key   = `${city}|${state}`;

      if (!groups[key]) {
        groups[key] = { city, state, count: 0, requests: [], latestDate: null };
      }

      groups[key].count++;
      groups[key].requests.push(req);

      // Track most recent request date for the summary row
      const d = req.requestedAt ? new Date(req.requestedAt) : null;
      if (d && (!groups[key].latestDate || d > groups[key].latestDate)) {
        groups[key].latestDate = d;
      }
    });

    return Object.values(groups).sort((a, b) => b.count - a.count);
  };

  // Title-case a space-separated string
  const capitalize = (str) => {
    if (!str) return '—';
    return str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Format Firestore Timestamp or ISO string to a readable date
  const formatDate = (val) => {
    if (!val) return '—';
    const d = val.toDate ? val.toDate() : new Date(val);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Format cents to USD string
  const formatCurrency = (cents) => {
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ===== EARLY RETURNS =====

  // Show nothing until role check resolves — avoids any flash of content
  if (checking) return null;

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner" />
        <p style={{ marginTop: '16px', color: '#555', fontFamily: 'Inter, sans-serif' }}>
          Loading admin data…
        </p>
      </div>
    );
  }

  // ===== RENDER =====
  const groups       = groupedRequests();
  const totalCities  = groups.length;
  const totalReqs    = expansionRequests.length;

  return (
    <div className="admin-page">

      {/* ===== GREEN HEADER BAR ===== */}
      <div className="admin-header">
        <span className="admin-header-title">Evermark Memorial — Admin</span>
        <span className="admin-header-sub">Internal only — do not share this URL</span>
      </div>

      <div className="admin-content">

        {/* Error banner */}
        {error && <div className="admin-error">{error}</div>}

        {/* ===== KEY METRICS ===== */}
        <section className="admin-section">
          <h2 className="admin-section-title">Key Metrics</h2>

          {metrics && (
            <div className="admin-metrics-grid">

              <div className="admin-metric-card">
                <div className="admin-metric-value">{metrics.customers.toLocaleString()}</div>
                <div className="admin-metric-label">Customers</div>
              </div>

              <div className="admin-metric-card">
                <div className="admin-metric-value">{metrics.providers.toLocaleString()}</div>
                <div className="admin-metric-label">Caretakers</div>
              </div>

              <div className="admin-metric-card">
                <div className="admin-metric-value">{metrics.bookings.toLocaleString()}</div>
                <div className="admin-metric-label">Total Bookings</div>
              </div>

              <div className="admin-metric-card">
                <div className="admin-metric-value">{metrics.paidBookings.toLocaleString()}</div>
                <div className="admin-metric-label">Paid Bookings</div>
              </div>

              {/* Revenue highlighted green */}
              <div className="admin-metric-card admin-metric-card--green">
                <div className="admin-metric-value">{formatCurrency(metrics.revenue)}</div>
                <div className="admin-metric-label">Total Revenue</div>
              </div>

              {/* Out-of-area highlighted amber */}
              <div className="admin-metric-card admin-metric-card--amber">
                <div className="admin-metric-value">{metrics.outOfArea.toLocaleString()}</div>
                <div className="admin-metric-label">Out-of-Area Signups</div>
              </div>

            </div>
          )}
        </section>

        {/* ===== EXPANSION REQUESTS ===== */}
        <section className="admin-section">
          <h2 className="admin-section-title">
            Expansion Requests
            <span className="admin-section-badge">
              {totalCities} {totalCities === 1 ? 'city' : 'cities'} · {totalReqs} {totalReqs === 1 ? 'request' : 'requests'}
            </span>
          </h2>

          {groups.length === 0 ? (
            <p className="admin-empty">No expansion requests yet.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>City</th>
                    <th>State</th>
                    <th>Requests</th>
                    <th>Most Recent</th>
                    <th style={{ width: '32px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => {
                    const key        = `${group.city}|${group.state}`;
                    const isExpanded = expandedCity === key;

                    return (
                      <React.Fragment key={key}>

                        {/* Summary row — clickable to expand */}
                        <tr
                          className={`admin-table-row admin-table-row--clickable ${isExpanded ? 'admin-table-row--open' : ''}`}
                          onClick={() => setExpandedCity(isExpanded ? null : key)}
                        >
                          <td>{capitalize(group.city)}</td>
                          <td>{group.state.toUpperCase()}</td>
                          <td><strong>{group.count}</strong></td>
                          <td>{group.latestDate ? formatDate(group.latestDate.toISOString()) : '—'}</td>
                          <td className="admin-expand-chevron">{isExpanded ? '▲' : '▼'}</td>
                        </tr>

                        {/* Individual requests — shown when row is expanded */}
                        {isExpanded && group.requests
                          .slice()
                          .sort((a, b) => new Date(b.requestedAt || 0) - new Date(a.requestedAt || 0))
                          .map(req => (
                            <tr key={req.id} className="admin-table-row-detail">
                              <td colSpan={5}>
                                <div className="admin-request-detail">
                                  <span className="admin-detail-email">{req.email || 'anonymous'}</span>
                                  <span className="admin-detail-date">{formatDate(req.requestedAt)}</span>
                                  {req.message && (
                                    <span className="admin-detail-message">"{req.message}"</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}

                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ===== RECENT SIGNUPS ===== */}
        <section className="admin-section">
          <h2 className="admin-section-title">Recent Signups <span className="admin-section-badge">last 10</span></h2>

          {recentSignups.length === 0 ? (
            <p className="admin-empty">No users yet.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Signed Up</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSignups.map((user, idx) => (
                    <tr
                      key={user.id}
                      className={idx % 2 === 0 ? 'admin-table-row-even' : 'admin-table-row-odd'}
                    >
                      <td>{user.firstName} {user.lastName}</td>
                      <td className="admin-cell-email">{user.email}</td>
                      <td>
                        <span className={`admin-role-badge admin-role-badge--${user.role || 'unknown'}`}>
                          {user.role === 'provider' ? 'Caretaker'
                            : user.role === 'admin' ? 'Admin'
                            : 'Customer'}
                        </span>
                      </td>
                      <td>{capitalize(user.city)}</td>
                      <td>{user.state ? user.state.toUpperCase() : '—'}</td>
                      <td>{formatDate(user.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

      </div>
      {/* End content */}
    </div>
  );
}

export default AdminDashboard;
